import { Type, type Static } from '@sinclair/typebox';

/**
 * Brief SEO : le contrat entre la recherche (SERP + pages scrapées + GSC) et la
 * rédaction. C'est la pièce maîtresse du pipeline (plan
 * `docs/to_implement/seo/seo-content-pipeline-plan.md` §4.3 job 2) : le rédacteur
 * ne reçoit rien d'autre, et le score de contenu (famille « Respect du brief »)
 * mesure l'article contre lui.
 *
 * Utilisé par : le moteur backend (`seo_content/engine`), l'admin Payload
 * (édition du brief avant rédaction), le scorer (`content-score`), le bench
 * rédacteur (briefs fixtures).
 */

export enum SeoSearchIntent {
  INFORMATIONAL = 'INFORMATIONAL',
  TRANSACTIONAL = 'TRANSACTIONAL',
  LOCAL = 'LOCAL',
  MIXED = 'MIXED',
}

export enum SeoContentType {
  LOCAL_GUIDE = 'LOCAL_GUIDE',
  GUIDE = 'GUIDE',
  TUTORIAL = 'TUTORIAL',
  COMPARISON = 'COMPARISON',
  NEWS = 'NEWS',
  FAQ = 'FAQ',
}

export enum SeoTargetKind {
  PROPELSIA = 'PROPELSIA',
  PROFESSIONAL = 'PROFESSIONAL',
}

export const SeoBriefLinkSlotSchema = Type.Object({
  url: Type.String(),
  title: Type.String(),
  /** Pourquoi ce lien est pertinent ici (guide l'ancre contextuelle). */
  reason: Type.Optional(Type.String()),
});
export type SeoBriefLinkSlot = Static<typeof SeoBriefLinkSlotSchema>;

export const SeoBriefFactSchema = Type.Object({
  /** Affirmation vérifiable, formulée telle qu'elle pourra être reprise. */
  statement: Type.String(),
  source_url: Type.Optional(Type.String()),
  source_title: Type.Optional(Type.String()),
});
export type SeoBriefFact = Static<typeof SeoBriefFactSchema>;

export const SeoBriefSectionSchema = Type.Object({
  level: Type.Union([Type.Literal(2), Type.Literal(3)]),
  title: Type.String(),
  /** Objectif de la section : ce que le lecteur doit en retirer. */
  purpose: Type.String(),
  /** Mots-clés secondaires / entités à placer naturellement dans la section. */
  keywords: Type.Optional(Type.Array(Type.String())),
  /** Questions (PAA) auxquelles la section répond. */
  questions: Type.Optional(Type.Array(Type.String())),
  /** Faits autorisés dans la section : phrases de `facts`, telles quelles (le brief écarte tout le reste). */
  facts: Type.Optional(Type.Array(Type.String())),
  /** Liens internes à placer dans cette section. */
  links: Type.Optional(Type.Array(SeoBriefLinkSlotSchema)),
  target_words: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type SeoBriefSection = Static<typeof SeoBriefSectionSchema>;

export const SeoBriefFaqItemSchema = Type.Object({
  question: Type.String(),
  /** Piste de réponse (faits, angle), pas la réponse rédigée. */
  answer_hint: Type.Optional(Type.String()),
});
export type SeoBriefFaqItem = Static<typeof SeoBriefFaqItemSchema>;

/**
 * Persona éditorial d'une cible (2026-09-06) : ce qui donne un point de vue au texte. Généré une
 * fois par site depuis ses propres données (jamais un gabarit : deux blogs ne doivent pas partager
 * une voix), relu par le pro dans ses réglages, puis réutilisé par tous les articles. Les partis pris
 * sont des opinions, jamais des chiffres : ils n'entrent pas en conflit avec la règle des faits.
 */
export const SeoPersonaSchema = Type.Object({
  /** Qui parle, en une phrase : « Camille, coloriste depuis douze ans à Lyon ». */
  who: Type.String(),
  /** Partis pris métier (« je refuse deux décolorations le même jour »). 2 à 4. */
  convictions: Type.Array(Type.String()),
  /** Idées reçues que l'auteur corrige souvent. 1 à 3. */
  misconceptions: Type.Array(Type.String()),
  /** Ce que l'auteur ne dit ou ne fait jamais (promesses, jargon, ton). 1 à 3. */
  never_says: Type.Array(Type.String()),
});
export type SeoPersona = Static<typeof SeoPersonaSchema>;

export const SeoBriefVoiceSchema = Type.Object({
  /** Ex. « concret, chaleureux, sans jargon ». */
  tone: Type.String(),
  /** Pronom d'énonciation : « je », « nous », « on », ou impersonnel. */
  write_as: Type.String(),
  /** Adresse au lecteur : « vous », « tu ». */
  write_to: Type.String(),
  /** Nom de l'entité qui signe (marque ou établissement). */
  brand_name: Type.String(),
  /** Une phrase de contexte sur l'auteur (activité, ville, positionnement). */
  about: Type.Optional(Type.String()),
  /** Persona éditorial (point de vue) ; absent = voix neutre. */
  persona: Type.Optional(SeoPersonaSchema),
});
export type SeoBriefVoice = Static<typeof SeoBriefVoiceSchema>;

export const SeoBriefCtaSchema = Type.Object({
  /** Intention du CTA (« réserver une coupe », « essayer Propelsia »). */
  goal: Type.String(),
  url: Type.Optional(Type.String()),
  /** Où le placer : en fin d'article par défaut. */
  placement: Type.Optional(Type.Union([Type.Literal('END'), Type.Literal('MIDDLE'), Type.Literal('NONE')])),
});
export type SeoBriefCta = Static<typeof SeoBriefCtaSchema>;

export const SeoBriefSchema = Type.Object({
  target_kind: Type.Enum(SeoTargetKind),
  keyword: Type.String({ minLength: 2 }),
  /** Titre H1 proposé (le rédacteur peut l'affiner, pas le remplacer). */
  title: Type.String({ minLength: 5 }),
  slug: Type.Optional(Type.String()),
  search_intent: Type.Enum(SeoSearchIntent),
  content_type: Type.Enum(SeoContentType),
  /** Zone visée par l'article (ville du mot-clé local), absente pour un sujet national. */
  area: Type.Optional(Type.String()),
  /** Angle différenciant par rapport aux pages qui rankent déjà. */
  angle: Type.String(),
  /**
   * Ce qui est propre à la zone visée (clientèle, accès, saisonnalité, habitudes locales) et que
   * l'article développe : deux articles « coupe homme Nice » et « coupe homme Antibes » ne doivent
   * pas être le même texte avec un autre nom de ville. Présent dès que `area` l'est.
   */
  local_angle: Type.Optional(Type.String()),
  /**
   * Ce que l'article AFFIRME, en une phrase (prise de position, pas un sujet) : « le prix d'un
   * balayage se joue sur la longueur et la patine, le reste se décide au diagnostic ». Sans chiffre.
   */
  thesis: Type.Optional(Type.String()),
  /**
   * Situation concrète pour ouvrir l'article (une cliente arrive avec une photo, une question reçue
   * au salon…) : le rédacteur ouvre dessus au lieu d'annoncer le plan. Sans chiffre.
   */
  opening_scene: Type.Optional(Type.String()),
  /** Idée reçue que l'article corrige. */
  misconception: Type.Optional(Type.String()),
  /** Public visé, en une phrase. */
  audience: Type.Optional(Type.String()),
  target_word_count: Type.Integer({ minimum: 100 }),
  /** Longueur médiane des top 5 (si connue) : sert au ratio LENGTH_VS_SERP. */
  serp_median_word_count: Type.Optional(Type.Integer({ minimum: 0 })),
  secondary_keywords: Type.Array(Type.String()),
  /** Entités / termes du champ sémantique relevés dans les pages qui rankent. */
  entities: Type.Array(Type.String()),
  /** Questions réelles des internautes (People Also Ask, forums). */
  questions: Type.Array(Type.String()),
  outline: Type.Array(SeoBriefSectionSchema, { minItems: 2 }),
  facts: Type.Array(SeoBriefFactSchema),
  /** Liens internes candidats, avant assignation par section. */
  internal_links: Type.Array(SeoBriefLinkSlotSchema),
  faq: Type.Array(SeoBriefFaqItemSchema),
  cta: SeoBriefCtaSchema,
  voice: SeoBriefVoiceSchema,
  /** Termes à ne jamais écrire (concurrents, allégations, vocabulaire médical…). */
  forbidden_terms: Type.Array(Type.String()),
  /** Hôtes considérés comme internes pour le maillage. */
  internal_hosts: Type.Array(Type.String()),
  /**
   * Hôtes vers lesquels l'article ne doit jamais lier : sites positionnés sur la requête (lus
   * dans la SERP), concurrents surveillés et hôtes interdits du profil. Règle bloquante
   * `FORBIDDEN_LINK`.
   */
  competitor_hosts: Type.Optional(Type.Array(Type.String())),
  /** Chemins des pages de conversion attendues (réserver, prestation…). */
  conversion_paths: Type.Optional(Type.Array(Type.String())),
  /**
   * Consignes éditoriales du profil (réglages du pro ou de l'équipe, consignes ponctuelles du
   * brief), recopiées pour le rédacteur (2026-10-21) : jusque-là seul le brief les lisait, et
   * « ne pas rédiger à la première personne » n'atteignait jamais l'article.
   */
  editorial_notes: Type.Optional(Type.Array(Type.String())),
});
export type SeoBrief = Static<typeof SeoBriefSchema>;
